import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AIChatPreviewCard } from "@/components/dashboard/AIChatPreviewCard";
import { ComplaintLifecycleCard } from "@/components/dashboard/ComplaintLifecycleCard";
import { DashboardWorkbench } from "@/components/dashboard/DashboardWorkbench";
import { MetricCards } from "@/components/dashboard/MetricCards";
import { RecentComplaintsCard } from "@/components/dashboard/RecentComplaintsCard";
import { getBoothDashboard, listComplaints, startIntake } from "@/lib/api";

const statusClassMap = {
  Created: "sp-pend",
  Acknowledged: "sp-ack",
  InReview: "sp-pend",
  Resolved: "sp-res",
  Archived: "sp-res",
};

export const BoothWorkspacePage = ({ session, language }) => {
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [selectedComplaintId, setSelectedComplaintId] = useState("");
  const [category, setCategory] = useState("Land Dispute");
  const [channel, setChannel] = useState("booth");
  const [chatInput, setChatInput] = useState("");

  const loadData = async () => {
    try {
      const [dashboardData, complaintData] = await Promise.all([
        getBoothDashboard(session.token),
        listComplaints(session.token),
      ]);
      setDashboard(dashboardData);
      setComplaints(complaintData);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to load booth dashboard");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const recentRows = complaints.slice(0, 10).map((item) => ({
    ...item,
    statusClass: statusClassMap[item.status] || "sp-pend",
    dateLabel: item.created_at ? new Date(item.created_at).toLocaleDateString("en-IN", { month: "short", day: "2-digit" }) : "-",
  }));

  const selectedComplaint = complaints.find((item) => item.complaint_id === selectedComplaintId) || complaints[0] || null;

  const metricCards = [
    {
      label: "Total Booth Complaints",
      value: dashboard?.total ?? 0,
      badge: "Booth scoped",
      badgeClass: "badge-gray",
    },
    {
      label: "Acknowledged",
      value: dashboard?.acknowledged ?? 0,
      badge: "Processed",
      badgeClass: "badge-green",
    },
    {
      label: "High Priority",
      value: dashboard?.level_1 ?? 0,
      badge: "Level 1",
      badgeClass: "badge-amber",
    },
    {
      label: "Resolved",
      value: dashboard?.resolved ?? 0,
      badge: "Completed",
      badgeClass: "badge-blue",
    },
  ];

  const startNewComplaint = async () => {
    try {
      const intake = await startIntake(session.token, {
        language,
        category,
        booth_id: session.booth_id,
        channel,
      });
      sessionStorage.setItem("aavedak-intake-session-id", intake.session_id);
      sessionStorage.setItem("aavedak-intake-category", category);
      sessionStorage.setItem("aavedak-intake-channel", channel);
      sessionStorage.setItem("aavedak-intake-greeting", intake.opening_question);
      navigate("/workspace/intake");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to start intake flow");
    }
  };

  return (
    <DashboardWorkbench
      session={session}
      workspaceTitle="Booth Operations Dashboard"
      navSections={[
        {
          title: "Booth Ops",
          items: [
            { key: "dashboard", label: "Dashboard", icon: "📊" },
            { key: "newcase", label: "Start New Case", icon: "🧾" },
            { key: "complaints", label: "Complaint Records", icon: "📁" },
          ],
        },
      ]}
      activeKey={activeNav}
      onNavigate={setActiveNav}
      topActions={
        <>
          <Button variant="outline" className="btn-sm" onClick={loadData} data-testid="booth-refresh-btn">Refresh</Button>
          <Button className="btn-sm btn-sm-orange" onClick={startNewComplaint} data-testid="booth-start-intake-btn">+ Start Intake</Button>
        </>
      }
    >
      {activeNav === "dashboard" ? (
        <>
          <MetricCards cards={metricCards} />
          <div className="two-col" data-testid="booth-dashboard-two-col">
            <RecentComplaintsCard
              title="Booth Recent Complaints"
              complaints={recentRows}
              onOpen={(complaintId) => {
                setSelectedComplaintId(complaintId);
                setActiveNav("complaints");
              }}
            />

            <ComplaintLifecycleCard
              complaint={selectedComplaint}
              onDownloadPdf={() => {
                toast.info("Download from complaint detail workflow");
              }}
              onAuditTrail={() => {
                setActiveNav("complaints");
              }}
            />
          </div>
        </>
      ) : null}

      {activeNav === "newcase" ? (
        <div className="two-col" data-testid="booth-newcase-layout">
          <div className="card" data-testid="booth-newcase-card">
            <div className="card-head">
              <h3>Initiate Complaint Intake</h3>
            </div>
            <div className="card-body form-grid">
              <select value={category} onChange={(event) => setCategory(event.target.value)} data-testid="booth-category-select">
                <option>Land Dispute</option>
                <option>Family Dispute</option>
                <option>Dowry</option>
                <option>Other</option>
              </select>
              <select value={channel} onChange={(event) => setChannel(event.target.value)} data-testid="booth-channel-select">
                <option value="booth">Booth Assisted</option>
                <option value="online">Online Assisted</option>
              </select>
              <Button onClick={startNewComplaint} data-testid="booth-start-newcase-button">Proceed to AI Interview</Button>
            </div>
          </div>

          <AIChatPreviewCard
            title="Booth AI Conversation Preview"
            messages={[
              { role: "agent", text: `Welcome to Aavedak. Please tell me the issue related to ${category}.` },
              { role: "agent", text: "I will ask follow-up details and then generate formal application." },
            ]}
            inputValue={chatInput}
            onInputChange={setChatInput}
            onSend={() => {
              toast.info("Use proceed button to open full intake mode");
            }}
          />
        </div>
      ) : null}

      {activeNav === "complaints" ? (
        <>
          <div className="card" data-testid="booth-complaints-list-card">
            <div className="card-head">
              <h3>Booth Complaint Register</h3>
            </div>
            <div className="card-body card-body-flat">
              {recentRows.map((item) => (
                <button
                  key={item.complaint_id}
                  className="complaint-row"
                  onClick={() => setSelectedComplaintId(item.complaint_id)}
                  data-testid={`booth-complaint-row-${item.complaint_id}`}
                >
                  <span className="comp-id">{item.complaint_id}</span>
                  <span className="comp-title">{item.category}</span>
                  <span className={`status-pill ${item.statusClass}`}>{item.status}</span>
                  <span className="comp-date">Level {item.current_level}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="card" data-testid="booth-complaint-summary-card">
            <div className="card-head">
              <h3>Selected Complaint Summary</h3>
            </div>
            <div className="card-body">
              {selectedComplaint ? (
                <>
                  <p><strong>ID:</strong> {selectedComplaint.complaint_id}</p>
                  <p><strong>Status:</strong> {selectedComplaint.status}</p>
                  <p><strong>Applicant:</strong> {selectedComplaint.applicant_name} ({selectedComplaint.applicant_mobile})</p>
                  <p><strong>Urgency:</strong> {selectedComplaint.urgency_flag ? "Yes" : "No"}</p>
                  <p><strong>Escalation:</strong> Level {selectedComplaint.current_level}</p>
                  <p className="draft-text">{selectedComplaint.drafted_application_text}</p>
                </>
              ) : (
                <p>No complaint selected.</p>
              )}
            </div>
          </div>
        </>
      ) : null}
    </DashboardWorkbench>
  );
};
